import React from 'react';
import { get, capitalize, startCase, defaultTo } from 'lodash';
import { PromotionDuration } from '@atlassian/cssxp-types-promotion';
import { Promotion, SubBenefitType, generatedTypesNew, Benefit, Maybe } from '@css-xp/graphtypes';
import { Currency } from '@css-xp/graphtypes/src/generated-types-new';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { SelectedPromotion } from '../../forms';

enum INVALID_PROMO_TYPE {
  NOT_AVAILABLE = 'Not available',
}

enum SUB_BENEFIT_TYPE {
  TRIAL = 'TRIAL',
}

interface PromotionOptionConfig {
  handleEmptyTitle?: boolean;
  showDuration?: boolean;
  showReason?: boolean;
  showBenefitNextLine?: boolean;
  showCycleInterval?: boolean;
  showLabelsInUpperCase?: boolean;
  showPercentageLabel?: boolean;
  showBenefitType?: boolean;
  showReasonNextLine?: boolean;
  isMonthlyPlan?: boolean;
  showLegacyInvoiceId?: boolean;
}

export enum DISCOUNT_LABELS {
  PROMO_CODE = 'Vendor promotion',
  PARTNER_DISCOUNT = 'Partner discount',
  DISCRETIONARY_DISCOUNT = 'Custom discount',
  LOYALTY_DISCOUNT = 'Loyalty discount',
  PARTNER_MARGIN = 'Partner discount',
  OTHER = 'Other',
}

export const isDiscretionaryDiscountType = (discountType: string) => {
  return discountType === DISCRETIONARY_DISCOUNT;
};

export const getSubBenefitType = (type: SubBenefitType) => {
  if (type === 'PERCENTAGE' || type === 'MULTI_PERCENTAGE') {
    return '%';
  } else if (type === 'TRIAL') {
    return 'TRIAL';
  } else {
    return '';
  }
};

export const DISCRETIONARY_DISCOUNT = 'DISCRETIONARY_DISCOUNT';

export const isDiscretionaryDiscountPromotion = (formData: any, prependKey = '') => {
  return get(formData, `${prependKey ? prependKey + '.' : ''}promotion.type`, '') === DISCRETIONARY_DISCOUNT;
};

export const getPromotionTitle = (
  promotion: Maybe<Promotion> | Maybe<generatedTypesNew.Promotion>,
  { handleEmptyTitle }: PromotionOptionConfig = {}
) => {
  return promotion?.title || (handleEmptyTitle ? INVALID_PROMO_TYPE.NOT_AVAILABLE : '');
};
export const pluraliseBillingCycle = (iterations: string | number) => {
  return 'billing cycle' + (+iterations > 1 ? 's' : '');
};
export const getPromotionDuration = (benefit: Benefit) => {
  return benefit?.duration === PromotionDuration.REPEATING && benefit?.iterations
    ? `${benefit.iterations} ${pluraliseBillingCycle(benefit.iterations)}`
    : capitalize(benefit?.duration ?? '');
};

export const getPromotionType = (promotion: Promotion) => {
  let promotionType = promotion && promotion.type && promotion.type.replace(/_/g, ' ');
  if (promotionType === 'PROMO CODE') {
    promotionType = 'VENDOR PROMOTION';
  }
  return defaultTo(startCase(promotionType?.toLowerCase() ?? ''), 'N/A');
};

export const getPartnerPromotionType = (promotion: Promotion) => {
  const promotionType =
    promotion && promotion.type ? DISCOUNT_LABELS[promotion.type as keyof typeof DISCOUNT_LABELS] : '';
  return defaultTo(promotionType, 'N/A');
};

export const getCustomPromotionReason = (promotion: Promotion) => {
  return (
    promotion?.applicationReason?.readableName
      ?.toLowerCase()
      .replace(/_/g, ' ')
      ?.split(' ')
      ?.map((value) => capitalize(value))
      ?.join(' ') || 'N/A'
  );
};

const getBenefitLabel = (benefit: Benefit) => {
  if (benefit.subBenefitType === SubBenefitType.FLAT) {
    return 'Amount';
  }
  return 'Benefit';
};

export const renderPromotionBenefits = (
  promotion: Promotion,
  {
    showDuration,
    showBenefitNextLine,
    showLabelsInUpperCase,
    showPercentageLabel,
    showBenefitType,
    showLegacyInvoiceId,
  }: PromotionOptionConfig = {}
) => {
  const promotionBenefits = (promotion && promotion.benefits) || [];
  const benefitLength = promotionBenefits.length;
  // @ts-ignore
  const legacyInvoiceNumber = promotion?.legacyInvoiceNumber;
  return (
    <span>
      {promotionBenefits.map((benefit, index) => {
        if (!benefit) {
          return null;
        }
        const duration = showDuration && getPromotionDuration(benefit);

        const wrapperContent = (
          <>
            {showPercentageLabel ? `${benefit?.subBenefitType?.toUpperCase()}` : getBenefitLabel(benefit)}:{' '}
            {getCurrencyPlaceholder(
              benefit.subBenefitType as SubBenefitType,
              get(promotion, `eligibilityRules.currency[${index}]`, '')
            )}
            {getBenefitValueWithSubBenefitType(benefit, get(promotion, `eligibilityRules.currency[${index}]`, ''))}
            {showBenefitType && benefit.subBenefitType !== SubBenefitType.FLAT && benefit?.benefitType?.toLowerCase()}
            {showDuration && `${showLabelsInUpperCase ? ', DURATION: ' : ', Duration: '}` + duration}
            <br />
            {showLegacyInvoiceId && !!legacyInvoiceNumber && <span> Legacy invoice ID: {legacyInvoiceNumber}</span>}
          </>
        );
        if (!showBenefitNextLine && benefitLength === 1) {
          return <span key={index}>{wrapperContent}</span>;
        } else {
          return <p key={index}>{wrapperContent}</p>;
        }
      })}
    </span>
  );
};

export const getPartnerPromotionDuration = (benefit: Benefit, promoIndex: number, isMonthlyPlan?: boolean) => {
  let durationContentRepeat = promoIndex === 0 ? 'for the first ' : 'discount for the next ';
  if (isMonthlyPlan) {
    durationContentRepeat +=
      benefit?.iterations && benefit?.iterations === 1 ? `${benefit.iterations} month` : `${benefit.iterations} months`;
  } else {
    durationContentRepeat +=
      benefit?.iterations && benefit?.iterations === 1 ? `${benefit.iterations} year` : `${benefit.iterations} years`;
  }

  if (benefit?.duration === PromotionDuration.REPEATING && benefit?.iterations) {
    return durationContentRepeat;
  } else if (benefit?.duration === PromotionDuration.ONCE) {
    return 'ONCE';
  }
  return 'discount forever after that';
};

const getCurrencyPlaceholder = (subBenefitType: SubBenefitType, currency: string) => {
  if (subBenefitType === SubBenefitType.FLAT) {
    return `${currency} `;
  }
  return '';
};

export const getCurrencyConvertedValue = (value: Maybe<number>, currency: string) => {
  if (currency === Currency.USD && value) {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(Number(Number(value / 100).toFixed(2)));
  }
  return value ?? '';
};

const getBenefitValueFromValues = (benefit: Benefit) => {
  const benefitValues = benefit?.values ?? [];
  if (benefitValues.length > 0 && benefitValues[0]?.value) {
    return benefitValues[0].value;
  } else {
    return benefit.value;
  }
};

const getAppliedOnText = (appliedOn?: string | null) => {
  switch (appliedOn) {
    case 'RENEW':
      return 'for Renewal';
    case 'UPSELL':
      return 'for Upgrade';
    default:
      return '';
  }
};

export const getBenefitValueWithSubBenefitType = (benefit: Benefit, currency: string) => {
  if (benefit.subBenefitType === SubBenefitType.MULTI_PERCENTAGE) {
    const benefitValues = benefit?.values ?? [];
    return `${benefitValues
      .map((benefitsValue) => {
        if (benefitsValue?.value) {
          return `${benefitsValue?.value}${getSubBenefitType(
            benefit.subBenefitType as SubBenefitType
          )} ${getAppliedOnText(benefitsValue?.appliedOn)}`;
        } else {
          return `No discount ${getAppliedOnText(benefitsValue?.appliedOn)}`;
        }
      })
      .join(', ')} `;
  } else if (benefit.subBenefitType === SubBenefitType.FLAT && currency) {
    return `${getCurrencyConvertedValue(getBenefitValueFromValues(benefit), currency)} `;
  } else if (benefit.subBenefitType === SubBenefitType.NONE) {
    return 'No discount ';
  }

  return `${getBenefitValueFromValues(benefit)}${getSubBenefitType(benefit.subBenefitType as SubBenefitType)} `;
};

export const renderPartnerPromotionBenefits = (
  promotion: Promotion,
  { showDuration, isMonthlyPlan }: PromotionOptionConfig = {}
) => {
  const promotionBenefits = (promotion && promotion.benefits) || [];

  return (
    <span>
      {promotionBenefits.map((benefit, index) => {
        if (!benefit) {
          return null;
        }
        const duration = getPartnerPromotionDuration(benefit, index, isMonthlyPlan);

        return (
          <p data-testid={`benefit-next-line_${index}`} key={index}>
            {getCurrencyPlaceholder(
              benefit.subBenefitType as SubBenefitType,
              get(promotion, `eligibilityRules.currency[${index}]`, '')
            )}
            {getBenefitValueWithSubBenefitType(benefit, get(promotion, `eligibilityRules.currency[${index}]`, ''))}
            {showDuration && duration}
          </p>
        );
      })}
    </span>
  );
};

export const getPromotionText = (promotion: SelectedPromotion) => {
  const promotionBenefits = (promotion && promotion.benefits) || [];
  if (promotionBenefits[0]?.subBenefitType === SUB_BENEFIT_TYPE.TRIAL) {
    return [`Benefit: ${promotionBenefits[0].value} days ${promotionBenefits[0].subBenefitType.toLowerCase()}`];
  }
  return promotionBenefits
    .map((benefit) => {
      if (!benefit) return null;
      return `${sentenceCase(benefit?.subBenefitType) || 'Benefit'}: ${benefit.value}${getSubBenefitType(
        benefit.subBenefitType as SubBenefitType
      )}, Duration: ${getPromotionDuration(benefit)}`;
    })
    .filter((each) => !!each);
};

export const getPromotionCode = (promotion: generatedTypesNew.Promotion) => {
  if (promotion?.type === 'PROMO_CODE' && !!promotion?.promotionCode) return promotion.promotionCode;
  return null;
};

export const renderPromotionValue = (
  promotion: Maybe<Promotion> | Maybe<generatedTypesNew.Promotion>,
  {
    showDuration,
    showReason,
    showBenefitNextLine,
    showCycleInterval,
    showLabelsInUpperCase,
    showPercentageLabel,
    showReasonNextLine = true,
    showBenefitType = true,
    showLegacyInvoiceId = false,
  }: PromotionOptionConfig = {}
) => {
  if (!promotion) {
    return <>{INVALID_PROMO_TYPE.NOT_AVAILABLE}</>;
  }
  const promotionBenefits = (promotion && promotion.benefits) || [];

  const promotionType = getPromotionType(promotion);
  const promotionCode = getPromotionCode(promotion as generatedTypesNew.Promotion);
  const cycleInterval =
    showCycleInterval &&
    promotion?.cycleInterval
      ?.map((interval: Maybe<generatedTypesNew.CycleInterval>) => sentenceCase(interval))
      ?.join(', ');
  const cycleIntervalRow = cycleInterval && <p>Cycle interval: {cycleInterval}</p>;
  const renderedPromoCode = promotionCode && `, Code: ${promotionCode}`;

  if (promotionBenefits[0]?.subBenefitType === SUB_BENEFIT_TYPE.TRIAL) {
    return (
      <>
        {!!promotionType && (
          <span>
            {showLabelsInUpperCase ? 'TYPE' : 'Type'}: {promotionType}
            {renderedPromoCode}
          </span>
        )}
        , {showLabelsInUpperCase ? 'BENEFIT' : 'Benefit'}: {promotionBenefits[0].value} days{' '}
        {promotionBenefits[0].subBenefitType.toLowerCase()}
        {cycleIntervalRow}
      </>
    );
  }

  const benefitLength = promotion.benefits?.length ?? 0;

  return (
    <>
      {!!promotionType &&
        (!showBenefitNextLine && benefitLength === 1 ? (
          <span>
            {showLabelsInUpperCase ? 'TYPE' : 'Type'}: {promotionType}
            {renderedPromoCode},{' '}
          </span>
        ) : (
          <p>
            {showLabelsInUpperCase ? 'TYPE' : 'Type'}: {promotionType}
            {renderedPromoCode}
          </p>
        ))}
      {renderPromotionBenefits(promotion, {
        showDuration,
        showBenefitNextLine,
        showLabelsInUpperCase,
        showPercentageLabel,
        showBenefitType,
        showLegacyInvoiceId,
      })}
      {showReason &&
        promotion.isPromotionCustomisable &&
        (showReasonNextLine ? (
          <p>
            {showLabelsInUpperCase ? 'REASON' : 'Reason'}: {getCustomPromotionReason(promotion)}
          </p>
        ) : (
          <span>
            {showLabelsInUpperCase ? ', REASON' : ', Reason'}: {getCustomPromotionReason(promotion)}
          </span>
        ))}
      {cycleIntervalRow}
    </>
  );
};

export const renderPartnerPromotionValue = (
  promotion: Maybe<Promotion> | Maybe<generatedTypesNew.Promotion>,
  {
    showDuration,
    showReason,
    showCycleInterval,
    showLabelsInUpperCase,
    showReasonNextLine = true,
    isMonthlyPlan = true,
  }: PromotionOptionConfig = {}
) => {
  if (!promotion) {
    return INVALID_PROMO_TYPE.NOT_AVAILABLE;
  }
  const promotionBenefits = (promotion && promotion.benefits) || [];

  const promotionType = getPartnerPromotionType(promotion);
  const promotionCode = getPromotionCode(promotion as generatedTypesNew.Promotion);
  const cycleInterval =
    showCycleInterval &&
    promotion?.cycleInterval
      ?.map((interval: Maybe<generatedTypesNew.CycleInterval>) => sentenceCase(interval))
      ?.join(', ');
  const cycleIntervalRow = cycleInterval && <p>Cycle interval: {cycleInterval}</p>;
  const renderedPromoCode = promotionCode && `, Code: ${promotionCode}`;

  if (promotionBenefits[0]?.subBenefitType === SUB_BENEFIT_TYPE.TRIAL) {
    return (
      <>
        {!!promotionType && (
          <span>
            Discount Type: {promotionType}
            {renderedPromoCode}
          </span>
        )}
        , {showLabelsInUpperCase ? 'BENEFIT' : 'Benefit'}: {promotionBenefits[0].value} days{' '}
        {promotionBenefits[0].subBenefitType.toLowerCase()}
        {cycleIntervalRow}
      </>
    );
  }

  return (
    <>
      {!!promotionType && (
        <p>
          Discount Type: {promotionType}
          {renderedPromoCode}
        </p>
      )}
      {renderPartnerPromotionBenefits(promotion, {
        showDuration,
        isMonthlyPlan,
      })}
      {showReason &&
        promotion.isPromotionCustomisable &&
        (showReasonNextLine ? (
          <p data-testid="reason-next-line">
            {showLabelsInUpperCase ? 'REASON' : 'Reason'}: {getCustomPromotionReason(promotion)}
          </p>
        ) : (
          <span>
            {showLabelsInUpperCase ? ', REASON' : ', Reason'}: {getCustomPromotionReason(promotion)}
          </span>
        ))}
      {cycleIntervalRow}
    </>
  );
};

export const renderPromotionOption = (
  promotion: Promotion | generatedTypesNew.Promotion,
  config: PromotionOptionConfig = {}
) => {
  const defaultConfig = {
    handleEmptyTitle: false,
    showDuration: true,
    showReason: true,
    showBenefitNextLine: false,
    showReasonNextLine: true,
    showCycleInterval: false,
    showLabelsInUpperCase: false,
    showPercentageLabel: false,
    showBenefitType: true,
  };
  const finalConfig = {
    ...defaultConfig,
    ...config,
  };
  return (
    <>
      <div>
        <strong>{getPromotionTitle(promotion, finalConfig)}</strong>
      </div>
      {renderPromotionValue(promotion, finalConfig)}
    </>
  );
};

const getCustomDuration = (promotion: SelectedPromotion) => {
  return promotion?.customFormField?.duration?.value === PromotionDuration.REPEATING &&
    promotion?.customFormField?.iterations
    ? `${promotion?.customFormField?.iterations} ${pluraliseBillingCycle(promotion?.customFormField?.iterations)}`
    : capitalize(promotion?.customFormField?.duration?.value as string);
};

export const renderCustomPromotionDetails = (promotion: SelectedPromotion) => {
  const type = startCase(DISCRETIONARY_DISCOUNT.toLowerCase().replace(/_/g, ' '));
  return (
    <span>
      TYPE: {type}, PERCENTAGE: {promotion?.customFormField?.value}%, DURATION: {getCustomDuration(promotion)}, REASON:{' '}
      {`${startCase(promotion?.customFormField?.reason?.label?.toLowerCase()) || 'N/A'}`}
    </span>
  );
};

export const getCustomPromotionText = (promotion: SelectedPromotion) => {
  return `Percentage: ${promotion?.customFormField?.value || 0}%, Duration: ${getCustomDuration(promotion)}`;
};
